import { useState, useCallback, useRef } from 'react';
import Supercluster from 'supercluster';
import type { Feature, Point } from 'geojson';
import { Stop } from '@app/shared/models/tour.type';
import { PlaceDetails } from '@app/shared/state/user-added-stops.atom';

export interface ClusterData {
  id: number;
  type: string;
  geometry: { type: string; coordinates: [number, number] };
  properties: any;
  stopsData: Stop[];
  stopIds: string[];
  stopTitles: string[];
  userAddedCount: number;
  tourStopCount: number;
}

export interface MarkerData {
  type: string;
  geometry: { type: string; coordinates: [number, number] };
  properties: {
    cluster: false;
    id: string;
    stopData: Stop | null;
    placeData: PlaceDetails | null;
    isUserAdded: boolean;
    locked?: boolean;
  };
}

interface UseSuperclusterProps {
  mapRef: React.MutableRefObject<any>;
  stops: Stop[];
  userAddedPlaces: PlaceDetails[];
  tourId: string;
  isDemo: boolean;
  demoLimit: number;
}

interface ClusterState {
  clusters: any[];
  individualMarkers: any[];
  clusteredUserPlaceIds: Set<string>;
  animatedMarkerIds: string[];
  animatedClusterIds: (string | number)[];
  supercluster: Supercluster | null;
}

interface UseSuperclusterResult extends ClusterState {
  updateClusters: (zoom: number) => void;
}

const INITIAL_STATE: ClusterState = {
  clusters: [],
  individualMarkers: [],
  clusteredUserPlaceIds: new Set(),
  animatedMarkerIds: [],
  animatedClusterIds: [],
  supercluster: null,
};

export function useSupercluster({
  mapRef,
  stops,
  userAddedPlaces,
  tourId,
  isDemo,
  demoLimit,
}: UseSuperclusterProps): UseSuperclusterResult {
  const [state, setState] = useState<ClusterState>(INITIAL_STATE);

  const prevIndividualMarkersRef = useRef<any[]>([]);
  const prevClustersRef = useRef<any[]>([]);

  // Keep a ref to userAddedPlaces to avoid stale closures
  const userAddedPlacesRef = useRef(userAddedPlaces);
  userAddedPlacesRef.current = userAddedPlaces;

  const updateClusters = useCallback((zoom: number) => {
    if (!mapRef.current) return;

    const userPlaces = userAddedPlacesRef.current;
    if (!stops?.length && !userPlaces.length) return;

    if (isDemo) {
      const allStops = stops || [];

      const demoIndividual = allStops.slice(0, demoLimit).map((stop) => ({
        type: 'Feature',
        properties: {
          cluster: false,
          id: stop.id,
          stopData: stop,
          locked: false,
          isUserAdded: false,
        },
        geometry: {
          type: 'Point',
          coordinates: [stop.stop_location.longitude, stop.stop_location.latitude],
        },
      }));

      const clusterableStops = allStops.slice(demoLimit).map((stop) => ({
        type: 'Feature',
        properties: {
          cluster: false,
          id: stop.id,
          stopData: stop,
          locked: true,
          isUserAdded: false,
        },
        geometry: {
          type: 'Point',
          coordinates: [stop.stop_location.longitude, stop.stop_location.latitude],
        },
      }));

      const index = new Supercluster({
        radius: 50,
        maxZoom: 17,
        map: (props: any) => ({ id: props.id, stopData: props.stopData, locked: props.locked }),
        reduce: (accumulated: any, props: any) => {
          accumulated.point_count_custom = (accumulated.point_count_custom || 0) + 1;
        },
      });

      index.load(clusterableStops as any);

      const bounds = mapRef.current.getBounds();
      const bbox: [number, number, number, number] = [
        bounds.getWest(),
        bounds.getSouth(),
        bounds.getEast(),
        bounds.getNorth(),
      ];

      const currentClustersAndMarkers = index.getClusters(bbox, zoom);

      const clustered = currentClustersAndMarkers
        .filter((c: any) => c.properties.cluster)
        .map((cluster: any) => {
          const leaves = index.getLeaves(cluster.id, Infinity);
          return {
            ...cluster,
            stopsData: leaves.map((leaf: any) => leaf.properties.stopData),
            stopIds: leaves.map((leaf: any) => leaf.properties.id),
            stopTitles: leaves.map((leaf: any) => leaf.properties.stopData?.s_title),
            userAddedCount: 0,
            tourStopCount: cluster.properties.point_count,
          };
        });

      const individualFromClusterable = currentClustersAndMarkers.filter((c: any) => !c.properties.cluster).map((c: any) => ({
        ...c,
        properties: { ...c.properties, locked: c.properties.locked ?? true },
      }));

      setState({
        supercluster: index,
        clusters: clustered,
        individualMarkers: [...demoIndividual, ...individualFromClusterable] as any,
        clusteredUserPlaceIds: new Set(),
        animatedMarkerIds: [],
        animatedClusterIds: [],
      });
      return;
    }

    // Normal mode
    const map = mapRef.current;
    const bounds = map.getBounds();
    const bbox: [number, number, number, number] = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ];

    const tourStopPoints: Feature<Point>[] = (stops || []).map((stop) => ({
      type: 'Feature',
      properties: {
        cluster: false,
        id: stop.id,
        stopData: stop,
        placeData: null,
        isUserAdded: false,
      },
      geometry: {
        type: 'Point',
        coordinates: [stop.stop_location.longitude, stop.stop_location.latitude],
      },
    }));

    const userPlacePoints: Feature<Point>[] = userPlaces.map((place: any) => ({
      type: 'Feature',
      properties: {
        cluster: false,
        id: `user_${place.place_id}`,
        stopData: null,
        placeData: place,
        isUserAdded: true,
      },
      geometry: {
        type: 'Point',
        coordinates: [place.geometry.location.lng, place.geometry.location.lat],
      },
    }));

    const allPoints = [...tourStopPoints, ...userPlacePoints];
    const filteredPoints = allPoints.filter((p: any) => {
      if (!p.properties.isUserAdded && p.properties?.stopData?.show_marker === false) {
        return false;
      }
      return true;
    });

    const index = new Supercluster({
      radius: 50,
      maxZoom: 17,
      map: (props: any) => ({
        id: props.id,
        stopData: props.stopData,
        placeData: props.placeData,
        isUserAdded: props.isUserAdded,
      }),
      reduce: (accumulated: any, props: any) => {
        accumulated.point_count_custom = (accumulated.point_count_custom || 0) + 1;
        accumulated.userAddedCount = (accumulated.userAddedCount || 0) + (props.isUserAdded ? 1 : 0);
        accumulated.tourStopCount = (accumulated.tourStopCount || 0) + (props.isUserAdded ? 0 : 1);
      },
    });

    index.load(filteredPoints as any);

    const currentClustersAndMarkers = index.getClusters(bbox, zoom);

    const clustered = currentClustersAndMarkers
      .filter((c: any) => c.properties.cluster)
      .map((cluster: any) => {
        const leaves = index.getLeaves(cluster.id, Infinity);
        const userAddedLeaves = leaves.filter((leaf: any) => leaf.properties.isUserAdded);
        const tourStopLeaves = leaves.filter((leaf: any) => !leaf.properties.isUserAdded);
        return {
          ...cluster,
          stopsData: tourStopLeaves.map((leaf: any) => leaf.properties.stopData),
          stopIds: leaves.map((leaf: any) => leaf.properties.id),
          stopTitles: tourStopLeaves.map((leaf: any) => leaf.properties.stopData?.s_title),
          userAddedCount: userAddedLeaves.length,
          tourStopCount: tourStopLeaves.length,
        };
      });

    const individual = currentClustersAndMarkers.filter((c: any) => !c.properties.cluster && !c.properties.isUserAdded);

    // Detect new/changed individual markers for animation
    const prevMarkerIds = prevIndividualMarkersRef.current.map((m: any) => m.properties.id);
    const newMarkerIds = individual.map((m: any) => m.properties.id);
    const addedMarkerIds = newMarkerIds.filter((id: any) => !prevMarkerIds.includes(id));

    // Detect new/changed clusters for animation
    const prevClusterInfo = prevClustersRef.current.map((c: any) => ({
      id: c.id,
      count: c.properties.point_count,
    }));
    const newClusterInfo = clustered.map((c: any) => ({
      id: c.id,
      count: c.properties.point_count,
    }));
    const addedClusterIds = newClusterInfo
      .filter((newC: any) => {
        const validId = typeof newC.id === 'string' || typeof newC.id === 'number';
        if (!validId) return false;
        const prevC = prevClusterInfo.find((pc: any) => pc.id === newC.id);
        return !prevC || prevC.count !== newC.count;
      })
      .map((c: any) => c.id as string | number);

    // Track which user-added places are in clusters
    const userIdsInClusters = new Set<string>();
    clustered.forEach((cluster: any) => {
      cluster.stopIds.forEach((id: string) => {
        if (id.startsWith('user_')) {
          userIdsInClusters.add(id);
        }
      });
    });

    // Update prev refs for next animation diff
    prevIndividualMarkersRef.current = individual;
    prevClustersRef.current = clustered;

    // Single setState = single re-render (was 6 separate setStates before)
    setState(prev => ({
      supercluster: index,
      clusters: clustered,
      individualMarkers: individual as any,
      clusteredUserPlaceIds: userIdsInClusters,
      animatedMarkerIds: addedMarkerIds.length > 0 ? addedMarkerIds : prev.animatedMarkerIds,
      animatedClusterIds: addedClusterIds.length > 0 ? addedClusterIds : prev.animatedClusterIds,
    }));
  }, [stops, mapRef, isDemo, demoLimit, tourId]);

  return {
    ...state,
    updateClusters,
  };
}
